import { CONFIGS, useConfigQuery } from '@buildwise/libs/configurations';
import useUserData from 'hooks/useUserData';
import { useMemo } from 'react';
import { Box } from '@mui/system';
import { Step, StepConnector, StepLabel, Stepper, Typography } from '@mui/material';
import { theme } from 'config/theme';
import CheckboxFillIcon from 'remixicon-react/CheckboxFillIcon';
import CheckboxBlankLineIcon from 'remixicon-react/CheckboxBlankLineIcon';
import CheckboxIndeterminateFillIcon from 'remixicon-react/CheckboxIndeterminateFillIcon';
import CheckboxIndeterminateLineIcon from 'remixicon-react/CheckboxIndeterminateLineIcon';
import { styled } from '@mui/material/styles';
import stepConnectorClasses from '@mui/material/StepConnector/stepConnectorClasses';

type RenderSuggestionStatusProps = {
  status: string;
};

function RenderSuggestionStatus({ status }: RenderSuggestionStatusProps) {
  const { companyId } = useUserData();
  const { activeUniqueEntries } = useConfigQuery({
    companyId,
    configName: CONFIGS.SUGGESTION_STATUS_TYPES.configName,
  });
  const { activeStep, isNotPlannedStatusConfigured, notPlannedStatusIndex, statusEntries } =
    useMemo(() => {
      let isNotPlannedStatusConfigured = false;
      let activeStep = 1;
      let notPlannedStatusIndex = -1;

      const statusEntries = activeUniqueEntries.filter((entry, index) => {
        if (entry.entryCode === 'Not Planned') {
          isNotPlannedStatusConfigured = true;
          notPlannedStatusIndex = index - 1;

          return false;
        }

        if (entry.entryCode === status) {
          activeStep = index + 1;
        }

        return true;
      });

      return {
        activeStep,
        isNotPlannedStatusConfigured,
        notPlannedStatusIndex,
        statusEntries,
      };
    }, [activeUniqueEntries, status]);

  return (
    <Box width="100%" mb={2} height={isNotPlannedStatusConfigured ? 130 : 'auto'}>
      <Stepper
        alternativeLabel
        activeStep={status === 'Not Planned' ? notPlannedStatusIndex : activeStep}
        sx={{ mt: 3 }}
        connector={<Connector />}
      >
        {statusEntries.map((entry, index) => (
          <Step key={entry.id} sx={{ position: 'relative' }}>
            {index === notPlannedStatusIndex && (
              <>
                <Box
                  sx={(theme) => ({
                    position: 'absolute',
                    height: 2,
                    width: 'calc(100% - 12px)',
                    backgroundColor:
                      status === 'Not Planned'
                        ? theme.palette.custom.superNova
                        : theme.palette.custom.blueChalk,
                    transform: 'translateX(calc(-50% + 14px)) rotate(35deg)',
                    transformOrigin: 'top left',
                    top: 12,
                    left: 0,
                  })}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    top: 'calc(12px + 80px)',
                    left: '50%',
                    transform: 'translate(-50%, -8px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                  }}
                >
                  {status === 'Not Planned' ? (
                    <CheckboxIndeterminateFillIcon color={theme.palette.custom.superNova} />
                  ) : (
                    <CheckboxIndeterminateLineIcon color={theme.palette.custom.line} />
                  )}
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: 14,
                      mt: 1,
                      color:
                        status === 'Not Planned'
                          ? theme.palette.custom.pirateGold
                          : theme.palette.custom.mistBlue,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Not Planned
                  </Typography>
                </Box>
              </>
            )}
            <StepLabel
              StepIconComponent={() =>
                (status === 'Not Planned' && notPlannedStatusIndex >= index + 1) ||
                activeStep >= index + 1 ? (
                  <CheckboxFillIcon color={theme.palette.custom.greenBlue} />
                ) : (
                  <CheckboxBlankLineIcon color={theme.palette.custom.line} />
                )
              }
              sx={(theme) => ({
                '& .MuiStepLabel-label': {
                  fontWeight: '700 !important',
                  fontSize: 14,
                  color: `${
                    (status === 'Not Planned' && notPlannedStatusIndex >= index + 1) ||
                    activeStep >= index + 1
                      ? theme.palette.custom.tuna
                      : theme.palette.custom.mistBlue
                  } !important`,
                },
              })}
            >
              {entry.referenceObject?.label || entry.entryDescription || entry.entryCode}
            </StepLabel>
          </Step>
        ))}
      </Stepper>
    </Box>
  );
}

const Connector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: theme.palette.custom.greenBlue,
    },
  },
  [`& .${stepConnectorClasses.line}`]: {
    borderColor: theme.palette.custom.blueChalk,
    borderTopWidth: 2,
    borderRadius: 1,
    ...theme.applyStyles('dark', {
      borderColor: theme.palette.grey[800],
    }),
  },
}));

export default RenderSuggestionStatus;
